import React from 'react';
import { BarChart, Trash2, Layers, Server } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных проектов
 */
const ComparisonSidebar = ({ 
  savedProjects, 
  deleteSavedProject, 
  loadSavedProject,
  blockchainPlatforms
}) => {
  // Если нет сохраненных проектов
  if (!savedProjects || savedProjects.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение проектов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты проектов для сравнения.
        </p>
      </div>
    );
  }

  // Форматирование стоимости в доллары
  const formatUSD = (value) => `$${formatNumber(value, 0)}`;

  // Получение названия блокчейн-платформы по ID
  const getPlatformName = (platformId) => {
    const platform = blockchainPlatforms?.find(p => p.id === platformId);
    return platform ? platform.name : platformId;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение проектов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProjects.length} {savedProjects.length === 1 ? 'проект' : savedProjects.length < 5 ? 'проекта' : 'проектов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedProjects.map((project) => (
          <div 
            key={project.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {project.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedProject(project.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedProject(project.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span className="flex items-center">
                  <Server className="w-3.5 h-3.5 mr-1 text-indigo-500" />
                  Платформа:
                </span>
                <span className="font-medium">
                  {getPlatformName(project.formData.blockchainPlatform)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Контракты:</span>
                <span>
                  {project.formData.smartContracts} шт.
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Срок:</span>
                <span>
                  {project.formData.projectDuration} мес.
                </span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatUSD(project.results.totalCost)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedProjects.length >= 2 && (
        <div className="mt-5 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
          <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-3">Сравнение стоимости</h4>
          
          <div className="space-y-3">
            {savedProjects.slice(0, 5).map((project) => {
              // Находим максимальную стоимость для нормализации
              const maxCost = Math.max(...savedProjects.map(p => p.results.totalCost));
              const percentage = (project.results.totalCost / maxCost) * 100;
              
              return (
                <div key={project.id}>
                  <div className="flex items-center justify-between mb-1 text-xs">
                    <span className="font-medium text-indigo-700 dark:text-indigo-300 truncate max-w-[50%]">{project.name}</span>
                    <span className="text-indigo-600 dark:text-indigo-400">{formatUSD(project.results.totalCost)}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-indigo-500 dark:bg-indigo-400 h-2 rounded-full" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;